import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore } from "@intrasync/stores";

/**
 * Invoice Types per spec 06-invoice-management.md
 */
export type InvoiceType = "customer" | "vendor" | "direct_gl" | "ica";

/**
 * Invoice Status Flow per spec:
 * Customer: Draft → Posted → Sent → Partially Paid → Paid
 * Vendor: Entered → Pending Approval → Approved → Scheduled → Paid
 * Note: "Matched" status removed per 2025-12-18 accounting review
 */
export type InvoiceStatus =
  | "draft"
  | "entered"
  | "posted"
  | "sent"
  | "pending_approval"
  | "approved"
  | "scheduled"
  | "partially_paid"
  | "paid"
  | "void";

/**
 * Invoice Line Item
 */
export interface InvoiceLineItem {
  id: string;
  lineNumber: number;
  description: string;
  quantity: number;
  unitPrice: number;
  amount: number;
  glAccountId: string;
  glAccountName?: string;
  taxable: boolean;
}

/**
 * Invoice record per spec
 */
export interface Invoice {
  id: string;
  invoiceNumber: string;
  invoiceType: InvoiceType;

  // Relationships
  vendorId?: string;
  vendorName?: string;
  customerId?: string;
  customerName?: string;
  plantId: string;
  plantCode?: string;
  jobId?: string;
  jobNumber?: string;
  poId?: string;
  poNumber?: string;

  // Dates (IM-04)
  invoiceDate: string;
  dueDate: string;
  postingDate?: string; // May differ for period closes

  // Status
  status: InvoiceStatus;
  paymentTerms: number; // days

  // Amounts
  lineItems: InvoiceLineItem[];
  subtotal: number;
  taxAmount: number;
  totalAmount: number;
  amountPaid: number;
  amountDue: number;

  // Documents
  hasAttachments?: boolean;
  documentId?: string;

  // Duplicate detection (IM-01, IM-02)
  isDuplicate: boolean;
  duplicateOfInvoiceId?: string;

  // Audit
  notes?: string;
  createdBy: string;
  createdByName?: string;
  createdAt: Date;
  approvedBy?: string;
  approvedByName?: string;
  approvedAt?: Date;
  voidedBy?: string;
  voidedAt?: Date;
  voidReason?: string;
}

/**
 * Duplicate check result (IM-01)
 */
export interface DuplicateCheckResult {
  isDuplicate: boolean;
  existingInvoice?: {
    id: string;
    invoiceNumber: string;
    vendorId: string;
    vendorName: string;
    invoiceDate: string;
    totalAmount: number;
    status: InvoiceStatus;
  };
}

/**
 * Form data for creating/editing invoices
 */
export interface InvoiceFormData {
  invoiceType: InvoiceType;
  invoiceNumber: string;
  vendorId: string;
  customerId: string;
  plantId: string;
  jobId: string;
  poId: string;
  invoiceDate: string;
  dueDate: string;
  postingDate: string;
  paymentTerms: number;
  lineItems: InvoiceLineItem[];
  notes: string;
}

/**
 * Invoices Store
 *
 * Manages invoice data per spec 06-invoice-management.md
 * - Duplicate detection at entry (IM-01)
 * - Invoice number tied to vendor (IM-02)
 * - Direct-to-GL invoicing (IM-03)
 * - Date handling (IM-04)
 * - Customer invoice generation (IM-05)
 * - Invoice search (IM-06)
 * - Status tracking (IM-07)
 */
class InvoicesStore {
  rootStore: RootStore;

  // Filters
  invoiceTypeFilter: InvoiceType | "all" = "all";
  statusFilter: InvoiceStatus | "all" = "all";
  plantFilter: string | null = null;
  vendorFilter: string = "";
  customerFilter: string = "";
  searchTerm: string = "";
  dateRangeStart: string = "";
  dateRangeEnd: string = "";

  // Sort
  sortField: "invoiceDate" | "dueDate" | "totalAmount" | "invoiceNumber" = "invoiceDate";
  sortDirection: "asc" | "desc" = "desc";

  // UI State
  error: string | null = null;
  selectedInvoice: Invoice | null = null;

  // Form state
  formData: InvoiceFormData = this.getEmptyFormData();

  // Duplicate detection
  duplicateCheckResult: DuplicateCheckResult | null = null;
  isCheckingDuplicate = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ==================== ServiceStore Access ====================

  get invoicesServiceStore() {
    return this.rootStore.serviceStores?.["invoices"];
  }

  get invoices(): Invoice[] {
    return (this.invoicesServiceStore?.entities as Invoice[]) || [];
  }

  get loading(): boolean {
    return this.invoicesServiceStore?.isFindPending || false;
  }

  // ==================== Computed Properties ====================

  /**
   * Filtered and sorted invoices
   */
  get filteredInvoices(): Invoice[] {
    let filtered = [...this.invoices];

    // Type filter
    if (this.invoiceTypeFilter !== "all") {
      filtered = filtered.filter((i) => i.invoiceType === this.invoiceTypeFilter);
    }

    // Status filter
    if (this.statusFilter !== "all") {
      filtered = filtered.filter((i) => i.status === this.statusFilter);
    }

    // Plant filter
    if (this.plantFilter) {
      filtered = filtered.filter((i) => i.plantId === this.plantFilter);
    }

    // Vendor filter
    if (this.vendorFilter) {
      filtered = filtered.filter((i) =>
        i.vendorName?.toLowerCase().includes(this.vendorFilter.toLowerCase())
      );
    }

    // Customer filter
    if (this.customerFilter) {
      filtered = filtered.filter((i) =>
        i.customerName?.toLowerCase().includes(this.customerFilter.toLowerCase())
      );
    }

    // Search
    if (this.searchTerm) {
      const search = this.searchTerm.toLowerCase();
      filtered = filtered.filter(
        (i) =>
          i.invoiceNumber.toLowerCase().includes(search) ||
          i.vendorName?.toLowerCase().includes(search) ||
          i.customerName?.toLowerCase().includes(search) ||
          i.jobNumber?.toLowerCase().includes(search) ||
          i.poNumber?.toLowerCase().includes(search)
      );
    }

    // Date range
    if (this.dateRangeStart) {
      filtered = filtered.filter((i) => i.invoiceDate >= this.dateRangeStart);
    }
    if (this.dateRangeEnd) {
      filtered = filtered.filter((i) => i.invoiceDate <= this.dateRangeEnd);
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal: any;
      let bVal: any;

      switch (this.sortField) {
        case "invoiceDate":
          aVal = a.invoiceDate;
          bVal = b.invoiceDate;
          break;
        case "dueDate":
          aVal = a.dueDate;
          bVal = b.dueDate;
          break;
        case "totalAmount":
          aVal = a.totalAmount;
          bVal = b.totalAmount;
          break;
        case "invoiceNumber":
          aVal = a.invoiceNumber;
          bVal = b.invoiceNumber;
          break;
        default:
          aVal = a.invoiceDate;
          bVal = b.invoiceDate;
      }

      if (this.sortDirection === "asc") {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      }
      return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
    });

    return filtered;
  }

  /**
   * Summary counts by type
   */
  get invoicesByType(): Record<InvoiceType | "all", number> {
    const counts: Record<string, number> = {
      all: this.invoices.length,
      customer: 0,
      vendor: 0,
      direct_gl: 0,
      ica: 0,
    };

    this.invoices.forEach((i) => {
      counts[i.invoiceType]++;
    });

    return counts as Record<InvoiceType | "all", number>;
  }

  /**
   * Summary counts by status
   */
  get invoicesByStatus(): Record<string, number> {
    const counts: Record<string, number> = {};
    this.invoices.forEach((i) => {
      counts[i.status] = (counts[i.status] || 0) + 1;
    });
    return counts;
  }

  /**
   * Total outstanding (unpaid) amount
   */
  get totalOutstanding(): number {
    return this.invoices
      .filter((i) => i.status !== "paid" && i.status !== "void")
      .reduce((sum, i) => sum + i.amountDue, 0);
  }

  /**
   * Overdue invoices (past due date and not paid)
   */
  get overdueInvoices(): Invoice[] {
    const today = new Date().toISOString().split("T")[0];
    return this.invoices.filter(
      (i) =>
        i.dueDate < today &&
        i.status !== "paid" &&
        i.status !== "void"
    );
  }

  /**
   * Duplicate invoices flagged
   */
  get duplicateInvoices(): Invoice[] {
    return this.invoices.filter((i) => i.isDuplicate);
  }

  // ==================== Filter Actions ====================

  setInvoiceTypeFilter = (type: InvoiceType | "all") => {
    this.invoiceTypeFilter = type;
  };

  setStatusFilter = (status: InvoiceStatus | "all") => {
    this.statusFilter = status;
  };

  setPlantFilter = (plantId: string | null) => {
    this.plantFilter = plantId;
  };

  setVendorFilter = (vendor: string) => {
    this.vendorFilter = vendor;
  };

  setCustomerFilter = (customer: string) => {
    this.customerFilter = customer;
  };

  setSearchTerm = (term: string) => {
    this.searchTerm = term;
  };

  setDateRange = (start: string, end: string) => {
    this.dateRangeStart = start;
    this.dateRangeEnd = end;
  };

  setSort = (field: "invoiceDate" | "dueDate" | "totalAmount" | "invoiceNumber") => {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
    } else {
      this.sortField = field;
      this.sortDirection = "desc";
    }
  };

  clearFilters = () => {
    this.invoiceTypeFilter = "all";
    this.statusFilter = "all";
    this.plantFilter = null;
    this.vendorFilter = "";
    this.customerFilter = "";
    this.searchTerm = "";
    this.dateRangeStart = "";
    this.dateRangeEnd = "";
  };

  // ==================== Form Actions ====================

  getEmptyFormData(): InvoiceFormData {
    const today = new Date().toISOString().split("T")[0];
    const dueDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0];

    return {
      invoiceType: "vendor",
      invoiceNumber: "",
      vendorId: "",
      customerId: "",
      plantId: "",
      jobId: "",
      poId: "",
      invoiceDate: today,
      dueDate: dueDate,
      postingDate: today,
      paymentTerms: 30,
      lineItems: [],
      notes: "",
    };
  }

  resetForm = () => {
    this.formData = this.getEmptyFormData();
    this.duplicateCheckResult = null;
  };

  updateFormField = <K extends keyof InvoiceFormData>(
    field: K,
    value: InvoiceFormData[K]
  ) => {
    this.formData[field] = value;

    // Auto-calculate due date when payment terms change
    if (field === "paymentTerms" || field === "invoiceDate") {
      const invoiceDate = new Date(this.formData.invoiceDate);
      const dueDate = new Date(
        invoiceDate.getTime() + this.formData.paymentTerms * 24 * 60 * 60 * 1000
      );
      this.formData.dueDate = dueDate.toISOString().split("T")[0];
    }

    // Trigger duplicate check when invoice number or vendor changes
    if (field === "invoiceNumber" || field === "vendorId") {
      this.checkForDuplicate();
    }
  };

  // ==================== Line Items ====================

  addLineItem = () => {
    const newItem: InvoiceLineItem = {
      id: `line-${Date.now()}`,
      lineNumber: this.formData.lineItems.length + 1,
      description: "",
      quantity: 1,
      unitPrice: 0,
      amount: 0,
      glAccountId: "",
      taxable: false,
    };
    this.formData.lineItems.push(newItem);
  };

  updateLineItem = (index: number, updates: Partial<InvoiceLineItem>) => {
    if (index >= 0 && index < this.formData.lineItems.length) {
      const item = this.formData.lineItems[index];
      Object.assign(item, updates);

      // Auto-calculate amount
      if ("quantity" in updates || "unitPrice" in updates) {
        item.amount = item.quantity * item.unitPrice;
      }
    }
  };

  removeLineItem = (index: number) => {
    if (index >= 0 && index < this.formData.lineItems.length) {
      this.formData.lineItems.splice(index, 1);
      // Renumber remaining items
      this.formData.lineItems.forEach((item, i) => {
        item.lineNumber = i + 1;
      });
    }
  };

  get formSubtotal(): number {
    return this.formData.lineItems.reduce((sum, item) => sum + item.amount, 0);
  }

  get formTaxAmount(): number {
    // Simple tax calculation - would be more complex in production
    const taxableAmount = this.formData.lineItems
      .filter((item) => item.taxable)
      .reduce((sum, item) => sum + item.amount, 0);
    return taxableAmount * 0.0875; // 8.75% tax rate
  }

  get formTotal(): number {
    return this.formSubtotal + this.formTaxAmount;
  }

  // ==================== Duplicate Detection (IM-01) ====================

  /**
   * Check if invoice number already exists for vendor
   */
  checkForDuplicate = async () => {
    const { invoiceNumber, vendorId } = this.formData;

    if (!invoiceNumber || !vendorId) {
      this.duplicateCheckResult = null;
      return;
    }

    this.isCheckingDuplicate = true;

    try {
      // Check against loaded invoices
      const existing = this.invoices.find(
        (i) =>
          i.vendorId === vendorId &&
          i.invoiceNumber.toLowerCase() === invoiceNumber.toLowerCase() &&
          i.status !== "void"
      );

      runInAction(() => {
        if (existing) {
          this.duplicateCheckResult = {
            isDuplicate: true,
            existingInvoice: {
              id: existing.id,
              invoiceNumber: existing.invoiceNumber,
              vendorId: existing.vendorId || "",
              vendorName: existing.vendorName || "",
              invoiceDate: existing.invoiceDate,
              totalAmount: existing.totalAmount,
              status: existing.status,
            },
          };
        } else {
          this.duplicateCheckResult = { isDuplicate: false };
        }
        this.isCheckingDuplicate = false;
      });
    } catch {
      runInAction(() => {
        this.isCheckingDuplicate = false;
      });
    }
  };

  // ==================== Validation ====================

  /**
   * Validate invoice form
   */
  validateInvoice = (
    data: InvoiceFormData
  ): {
    isValid: boolean;
    errors: string[];
  } => {
    const errors: string[] = [];

    if (!data.invoiceNumber.trim()) {
      errors.push("Invoice number is required");
    }

    if (data.invoiceType === "vendor" && !data.vendorId) {
      errors.push("Vendor is required for vendor invoices");
    }

    if (data.invoiceType === "customer" && !data.customerId) {
      errors.push("Customer is required for customer invoices");
    }

    if (!data.plantId) {
      errors.push("Plant is required");
    }

    if (!data.invoiceDate) {
      errors.push("Invoice date is required");
    }

    if (!data.dueDate) {
      errors.push("Due date is required");
    }

    if (data.lineItems.length === 0) {
      errors.push("At least one line item is required");
    }

    if (data.invoiceType === "direct_gl") {
      const missingGL = data.lineItems.some((item) => !item.glAccountId);
      if (missingGL) {
        errors.push("All line items require a GL account for Direct-to-GL invoices");
      }
    }

    if (this.duplicateCheckResult?.isDuplicate) {
      errors.push("Duplicate invoice detected - invoice number already exists for this vendor");
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  };

  // ==================== CRUD Actions ====================

  selectInvoice = (invoiceId: string) => {
    this.selectedInvoice = this.invoices.find((i) => i.id === invoiceId) || null;
  };

  clearSelectedInvoice = () => {
    this.selectedInvoice = null;
  };

  openInvoiceDetail = (invoiceId: string) => {
    this.selectInvoice(invoiceId);
    this.rootStore.routerStore.openDialog({
      dialogName: "invoiceDetail",
      dialogContext: invoiceId,
    });
  };

  openCreateInvoice = (type: InvoiceType = "vendor") => {
    this.resetForm();
    this.formData.invoiceType = type;
    this.rootStore.routerStore.openDialog({
      dialogName: "createInvoice",
    });
  };

  /**
   * Void an invoice
   */
  voidInvoice = async (invoiceId: string, reason: string) => {
    try {
      await this.invoicesServiceStore?.patch(invoiceId, {
        status: "void",
        voidReason: reason,
      });
      runInAction(() => {
        this.selectedInvoice = null;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to void invoice";
      });
    }
  };

  // ==================== Status Helpers ====================

  getStatusLabel = (status: InvoiceStatus): string => {
    const labels: Record<InvoiceStatus, string> = {
      draft: "Draft",
      entered: "Entered",
      posted: "Posted",
      sent: "Sent",
      pending_approval: "Pending Approval",
      approved: "Approved",
      scheduled: "Scheduled",
      partially_paid: "Partially Paid",
      paid: "Paid",
      void: "Void",
    };
    return labels[status] || status;
  };

  getStatusColor = (
    status: InvoiceStatus
  ): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    const colors: Record<InvoiceStatus, any> = {
      draft: "default",
      entered: "info",
      posted: "primary",
      sent: "info",
      pending_approval: "warning",
      approved: "success",
      scheduled: "info",
      partially_paid: "warning",
      paid: "success",
      void: "error",
    };
    return colors[status] || "default";
  };

  getTypeLabel = (type: InvoiceType): string => {
    const labels: Record<InvoiceType, string> = {
      customer: "Customer Invoice",
      vendor: "Vendor Invoice",
      direct_gl: "Journal Entry",
      ica: "Intercompany (ICA)",
    };
    return labels[type] || type;
  };

  // ==================== Data Loading ====================

  loadInvoices = async () => {
    this.error = null;
    try {
      await this.invoicesServiceStore?.find({ query: { $limit: 500 } });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to load invoices";
      });
    }
  };

  reset = () => {
    this.clearFilters();
    this.selectedInvoice = null;
    this.resetForm();
    this.error = null;
  };
}

export default InvoicesStore;
